export type PaymentReceivingDestinationProjection = {
  destination_id: string;
  destination_code: string;
  display_name: string;
  provider_code: string | null;
  public_details: Record<string, unknown>;
  route_role: "PRIMARY" | "FALLBACK";
  priority: number;
};

export type PaymentMethodRuntimeProjection = {
  payment_method_code: string;
  payment_method_name: string;
  runtime_capability: string;
  settlement_mode: string;
  provider_required: boolean;
  ready: boolean;
  reason_code: string | null;
  primary_destination: PaymentReceivingDestinationProjection | null;
  fallback_destinations: PaymentReceivingDestinationProjection[];
};

export type PaymentRuntimePayload = {
  ok: boolean;
  contract: "GET_POS_PAYMENT_RUNTIME_V1" | string;
  organization_id: string;
  branch_id: string;
  branch_code: string;
  branch_name: string;
  commerce_node_id: string;
  commerce_channel_id: string;
  employee_id: string;
  payment_methods: PaymentMethodRuntimeProjection[];
  generated_at: string;
};

type GatewayEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

export type PaymentRuntimeGatewayResult = {
  ok: boolean;
  data: PaymentRuntimePayload | null;
  error: string | null;
};

export class PosPaymentRuntimeGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  async load(input: { deviceSecret: string; sessionToken: string }): Promise<PaymentRuntimeGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
        }),
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
      });

      const payload = (await response.json().catch(() => null)) as GatewayEnvelope<PaymentRuntimePayload> | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, data: null, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }

      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
